import { Box, Tooltip } from "@mui/material";
import StatusTag from "components/molecules/StatusTag/StatusTag";
import useDataGrid from "components/templates/dataGridTable";
import { format } from "date-fns";
import { useGetCertificateHistoriesQuery } from "graphqlBase/certificateHistory/__generated__/getCertificateHistories";
import {
  CertificateInfoForRecursionFragment,
  useGetCertificateBridgeUnitsLazyQuery,
} from "graphqlBase/getCertificateBridgeUnits/__generated__/getCertificateBridgeUnits";
import { Unit } from "graphqlBase/types";
import { getTranslatedCertificateTypeString } from "lib/helper/getTranslatedRowDataStrings";
import { useGetUserInfo } from "lib/userInfo";
import ordinaryColors from "themes/ordinaryColors";
import translations from "translations";
import useGetTenantNameById from "utils/useGetTenantNameById";
import { zerezIdFontStyle } from "utils/zerezIdStyle";
import { ReactComponent as NESSymbolIcon } from "components/icons/iconsSvg/NESSymbol.svg";
import { MigrationNoteTooltip } from "./MigrationNoteTooltip";
import StatusCell from "./StatusColumn/StatusCell";
import UnitsAndCertificatesLink, { getCertificateLinkInput } from "./UnitsAndCertificatesLink";
import { formatDate } from "translations/formatter";
import { hasActiveErrorCorrection } from "lib/helper/hasActiveErrorCorrection";

type ReplacementHistoryCertificate = CertificateOnUnit & {
  replacedByCertificate?: ReplacementHistoryCertificate | null;
  replacedCertificates?: ReplacementHistoryCertificate[] | null;
  parentCertificateForValidityExtension?: ReplacementHistoryCertificate | null;
};
export type CertificateOnUnit = CertificateInfoForRecursionFragment;

interface DetailPanelContentProps {
  hasActiveErrorReport?: boolean;
  isPlatformOperator?: boolean;
  row: Unit;
  styleForDetailView?: boolean;
  showReplacementChain?: boolean;
}

const DetailPanelContent = ({
  row: rowProp,
  isPlatformOperator,
  styleForDetailView,
  hasActiveErrorReport,
  showReplacementChain = false,
}: DetailPanelContentProps) => {
  const { tenantNameById } = useGetTenantNameById({ withDeniedTenants: true });
  const userInfo = useGetUserInfo();
  const isUserPlatformOperator = userInfo?.role === "PLATFORM_OPERATOR";
  const isCertificationBody = userInfo?.role === "CERTIFICATION_AUTHORITY";
  const [query, { data, error, loading }] = useGetCertificateBridgeUnitsLazyQuery({ fetchPolicy: "network-only" });
  const { data: certificateHistory } = useGetCertificateHistoriesQuery({
    variables: {
      where: {
        and: [
          { certificateId: { eq: data?.certificateBridgeUnits?.items?.[0]?.certificate?.id } },
          { inEditByTenantId: { neq: null } },
        ],
      },
      order: [{ modifiedAt: "DESC" }],
      take: 1,
    },
    fetchPolicy: "network-only",
    skip: !data,
  });
  const { DataGrid, useMakeColumns, apiRef } = useDataGrid<CertificateOnUnit, "CertificateBridgeUnit">({
    variables: {
      where: {
        unitId: { eq: rowProp.id },
      },
    },
    query,
    tableId: "DetailPanel",
    // persistance: "runTime",
  });
  const minWidthColumn = 150;
  const isImported = data?.certificateBridgeUnits?.items?.[0]?.certificate?.isImported;
  const modifiedAt = certificateHistory?.certificateHistories?.items?.[0]?.modifiedAt;
  const isModified = !!modifiedAt;
  const modifiedBy = certificateHistory?.certificateHistories?.items?.[0]?.inEditByTenantId;
  const isNorm4105 =
    data?.certificateBridgeUnits?.items?.[0]?.certificate?.certificateBridgeCertificateNormIssueDates?.[0]?.certificateNormIssueDateId?.toLocaleUpperCase() ===
      "A22CDC02-2FDE-42F9-B53C-08DE9C623926" ||
    data?.certificateBridgeUnits?.items?.[0]?.certificate?.certificateBridgeCertificateNormIssueDates?.[0]?.certificateNormIssueDateId?.toLocaleUpperCase() ===
      "B760FA9D-24FB-5412-8672-BB4E300E581A";
  const columns = useMakeColumns([
    {
      field: "CertificateNumber",
      headerName: translations.entities.certificate.certificateNumber,
      type: "string",
      flex: 2,
      minWidth: minWidthColumn,
      renderCell: ({ row }) => {
        return (
          <Box sx={{ display: "flex", flexDirection: "row", gap: "8px", alignItems: "center" }}>
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                marginTop: "-3px",
                wordBreak: "break-word",
              }}
            >
              {UnitsAndCertificatesLink(getCertificateLinkInput({ row, hasActiveErrorReport, isPlatformOperator }))}
            </span>
            {row.isImported && <MigrationNoteTooltip entity="certificate" />}
            {row.certificateFormType === "GRID_FORMING" && (
              <Tooltip title={translations.pages.certificationManagement.gridFormingUnitTooltip}>
                <NESSymbolIcon width="1.8rem" height="1.8rem" />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: "DocumentType",
      headerName: translations.entities.certificate.certificateType,
      type: "string",
      flex: 2,
      width: minWidthColumn,
      valueGetter: (params) => getTranslatedCertificateTypeString(params.row?.certificateType?.id),
    },
    {
      field: "CertificateCode",
      headerName: translations.entities.certificate.certificateCode,
      type: "string",
      flex: 2,
      minWidth: minWidthColumn,
      renderCell: ({ row }) => {
        return (
          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {(row.isImported && !row.isVerified) || row.withTG8Rev25Conformity ? (
              translations.globals.helpText.notAssigned
            ) : (
              <span style={zerezIdFontStyle}>{row.certificateCode}</span>
            )}
          </span>
        );
      },
    },
    {
      field: "Certification Body",
      headerName: translations.entities.certificate.certifyingAuthority,
      type: "string",
      flex: 2,
      minWidth: minWidthColumn,
      valueGetter: ({ row }) =>
        row.certificateAuthorityId && row.certificateAuthorityId !== "28d2afd0-4db5-5a06-b7d0-542eb13f528a" // get tenant name for all tenants but placeholder tenant
          ? tenantNameById[row.certificateAuthorityId]
          : translations.globals.helpText.notSpecified,
    },

    {
      field: "Status",
      headerName: translations.pages.certificationManagement.certificateStatus,
      flex: 3,
      minWidth: minWidthColumn,
      renderCell: ({ row }) => {
        const certificate = row;
        const statusId = certificate?.certificateValidityStatus?.id;
        const status = certificate?.certificateValidityStatus?.certificateValidityStatusName;

        // Handle replaced certificate info
        const replacedByCertificateId = certificate?.getReplacedByCertificate?.id;
        const replacedByCertificateNumber = certificate?.getReplacedByCertificate?.certificateNumber;

        // Handle extended certificate info
        const extendedByCertificateId = certificate?.parentCertificateForValidityExtension?.id;
        const extendedByCertificateNumber = certificate?.parentCertificateForValidityExtension?.certificateNumber;

        // Only override to "Valid (Extended By)" if the current status is actually "Valid" and there's extension info
        // Don't override if the certificate has been withdrawn or has another explicit status
        const adjustedStatus =
          statusId === "c47a0b7c-701b-5544-92b0-6bcb48aac633" && extendedByCertificateId && extendedByCertificateNumber
            ? "Valid (Extended By)"
            : status;

        return (
          <StatusCell
            status={adjustedStatus}
            replacedByCertificate={
              replacedByCertificateId && replacedByCertificateNumber
                ? {
                    certificateNumber: replacedByCertificateNumber,
                    id: replacedByCertificateId,
                  }
                : undefined
            }
            extendedByCertificate={
              extendedByCertificateId && extendedByCertificateNumber
                ? {
                    certificateNumber: extendedByCertificateNumber,
                    id: extendedByCertificateId,
                  }
                : undefined
            }
          />
        );
      },
      type: "dateTime",
    },
    isCertificationBody && !isNorm4105
      ? {
          field: "validity",
          headerName: translations.entities.certificate.certificateValidity,
          flex: 2,
          minWidth: minWidthColumn,
          valueGetter: ({ row }) => {
            const validfrom = row.validityStartDate;
            const validUntil = row.validityEndDate;
            return `${formatDate(new Date(validfrom ?? ""))} - ${formatDate(new Date(validUntil ?? ""))}`;
          },
          type: "string",
        }
      : {
          field: "createdAt",
          headerName: isImported
            ? translations.entities.role.columns.importedAt
            : translations.entities.role.columns.createdAt,
          flex: 2,
          minWidth: minWidthColumn,
          valueGetter: ({ row }) =>
            row.createdAt ? format(new Date(row.transformedOn ?? row.createdAt), "dd.MM.yyyy HH:mm") : "",
          type: "dateTime",
        },
    {
      field: "createdBy",
      headerName: isImported
        ? translations.entities.role.columns.importedBy
        : translations.entities.role.columns.createdBy,
      flex: 2,
      minWidth: minWidthColumn,
      valueGetter: ({ row }) =>
        row.transformedBy ? tenantNameById[row.transformedBy] : row.tenantId ? tenantNameById[row.tenantId] : "",
      type: "string",
    },
    {
      field: "modifiedAt",
      headerName: translations.entities.certificate.modifiedAt,
      flex: 2,
      minWidth: minWidthColumn,
      valueGetter: ({ row }) => (modifiedAt ? format(new Date(modifiedAt), "dd.MM.yyyy HH:mm") : ""),
      type: "dateTime",
    },
    {
      field: "modifiedBy",
      headerName: translations.entities.role.columns.modifiedBy,
      flex: 2,
      minWidth: minWidthColumn,
      valueGetter: () => modifiedBy && tenantNameById[modifiedBy],
      type: "string",
    },
    {
      field: "Certificate Check",
      headerName: translations.pages.certificationManagement.certificateCheck,
      flex: 3,
      minWidth: minWidthColumn,
      renderCell: ({ row }) => {
        const inCorrection = hasActiveErrorCorrection(row.issues);
        const isValidityStatusVoid = row.certificateValidityStatus?.certificateValidityStatusName === "Void";
        if (isValidityStatusVoid) {
          return null;
        }
        if (inCorrection) {
          return (
            <StatusTag
              title={translations.pages.certificationManagement.inCorrection}
              color={ordinaryColors.warningDark}
            />
          );
        }
        return row.isVerified ? (
          <StatusTag
            title={translations.pages.certificationManagement.verified}
            backgroundColor={ordinaryColors.sucess200}
            color={ordinaryColors.grey900}
            borderColor="transparent"
          />
        ) : (
          // <StatusTag title={translations.pages.certificationManagement.notVerified} color={ordinaryColors.errorMain} />
          <StatusTag
            title={translations.pages.certificationManagement.verificationPending}
            color={ordinaryColors.grey600}
          />
        );
      },
      type: "string",
    },
  ]);

  // Flatten, filter duplicates by id, then sort
  // Include both replaced certificates AND extended certificates (validity extension chain)
  const flattenCertificates = (certificate: ReplacementHistoryCertificate | CertificateOnUnit): CertificateOnUnit[] => {
    const flattenedCertificates: CertificateOnUnit[] = [certificate];
    const replacedByCertificate = (certificate as ReplacementHistoryCertificate).replacedByCertificate;
    const replacedCertificates = (certificate as ReplacementHistoryCertificate).replacedCertificates;
    const parentCertificateForValidityExtension = (certificate as ReplacementHistoryCertificate)
      .parentCertificateForValidityExtension;

    // Follow replacedByCertificate (replacement chain)
    if (replacedByCertificate) {
      flattenedCertificates.push(...flattenCertificates(replacedByCertificate));
    }

    // Follow replacedCertificates (replacement chain)
    if (replacedCertificates) {
      replacedCertificates.forEach((replacedCertificate) => {
        if (replacedCertificate) flattenedCertificates.push(...flattenCertificates(replacedCertificate));
      });
    }

    // Follow parentCertificateForValidityExtension (extension chain)
    if (parentCertificateForValidityExtension) {
      flattenedCertificates.push(...flattenCertificates(parentCertificateForValidityExtension));
    }

    return flattenedCertificates.sort((a, b) => (b.revision ?? 0) - (a.revision ?? 0));
  };

  // Only show certificates directly connected to this unit, not the entire replacement chain
  const certificatesFlat = (data?.certificateBridgeUnits?.items ?? [])
    // Use flattening logic only when showReplacementChain is true (Zertifikatsverlauf)
    // Otherwise only show direct certificates (unit overview subtable)
    .flatMap((item) => (showReplacementChain ? flattenCertificates(item.certificate) : [item.certificate]))
    .reduce(
      (acc, cert) => {
        if (!acc.map.has(cert.id)) {
          acc.map.set(cert.id, true);
          acc.list.push(cert);
        }
        return acc;
      },
      { map: new Map(), list: [] as CertificateOnUnit[] }
    )
    .list.sort((a, b) => {
      // Sort by createdAt (or transformedOn if present), descending (newest first)
      const dateA = new Date(a.transformedOn ?? a.createdAt ?? 0).getTime();
      const dateB = new Date(b.transformedOn ?? b.createdAt ?? 0).getTime();
      return dateB - dateA;
    });

  return (
    <Box
      sx={{
        display: styleForDetailView ? "flex" : "unset",
        justifyContent: styleForDetailView ? "center" : "unset",
        boxSizing: "border-box",
        width: "100%",
        background: styleForDetailView ? "none" : ordinaryColors.functionalGreyBack,
      }}
    >
      <Box sx={{ flex: 1, mx: "auto", width: "90%", p: styleForDetailView ? 0 : 1 }}>
        <Box
          sx={{
            background: styleForDetailView ? "none" : ordinaryColors.functionalGreyBack,
            display: styleForDetailView ? "flex" : "unset",
            justifyContent: styleForDetailView ? "center" : "unset",
          }}
        >
          <DataGrid
            sx={{
              background: styleForDetailView ? ordinaryColors.functionalWhite : ordinaryColors.functionalGreyBack,
              "& .MuiDataGrid-cell": {
                alignItems: "start",
                diplay: "flex",
              },
              "& .MuiDataGrid-main": {
                minHeight: "150px !important",
              },
            }}
            hasToolbar={false}
            autoHeight
            disableSelectionOnClick
            loading={loading}
            error={error}
            columns={columns}
            rows={certificatesFlat}
            noDataMessage={translations.globals.notifications.noDataAvailable}
            tableHeight="auto"
            tableTitle="Assets"
            apiRef={apiRef}
            totalCount={data?.certificateBridgeUnits?.totalCount}
            checkboxSelection={false}
            getRowHeight={() => "auto"}
            hideFooterPagination
            hideFooter
            columnVisibilityModel={{
              createdBy: isUserPlatformOperator,
              modifiedAt: isModified && !!modifiedAt,
              modifiedBy: isModified && isUserPlatformOperator,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default DetailPanelContent;
